import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { monthsToLabel } from '../mock';

// ---- PDF-safe money formatting -------------------------------------------
const PDF_SYMBOLS = { INR: 'Rs.', USD: '$', EUR: 'EUR', GBP: 'GBP' };

function pdfMoney(value, currency = 'INR') {
  const sym = PDF_SYMBOLS[currency] || currency;
  const num = Math.round(Number(value) || 0);
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(num);
  return `${sym} ${formatted}`;
}

function ascii(s) {
  if (s == null) return '';
  return String(s).replace(/[^\x20-\x7E]/g, '');
}

async function snapshot(id) {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById(id);
  if (!el) return null;
  try {
    // Lighter snapshot: scale 1.35 + jpeg output → smaller PDF, still crisp
    const canvas = await html2canvas(el, {
      backgroundColor: '#ffffff',
      scale: 1.35,
      useCORS: true,
      logging: false,
    });
    return { dataUrl: canvas.toDataURL('image/jpeg', 0.78), w: canvas.width, h: canvas.height };
  } catch (e) {
    console.warn('[pdfExport] snapshot failed for', id, e);
    return null;
  }
}

export async function exportEmiPdf({
  loanAmount,
  interestRate,
  tenureMonths,
  moratorium,
  extraEmi,
  prepayments,
  summary,
  schedule,
  plainSummary,
  currency = 'INR',
  interestMethod = 'reducing',
  advanced = {},
  chartElementIds,
}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const advOn = !!(advanced && (advanced.creditDaysEnabled || advanced.subventionEnabled));

  // ---- Aggregate prepayment amounts ----
  const monthlyExtraTotal = Math.round((Number(extraEmi) || 0) * (summary.monthsTaken || tenureMonths));
  const lumpSumTotal = (prepayments || []).reduce((s, p) => s + (Number(p?.amount) || 0), 0);
  const prepayGrandTotal = monthlyExtraTotal + lumpSumTotal;

  // Actual figures for the summary block
  const actualInterest = Math.round(summary.totalInterest || 0);
  const actualTotalPayment = Math.round(summary.totalPayment || 0);

  // Decide which chart snapshots to embed:
  // - When advance is ON we want Actual (plain) + With Advance side by side —
  //   pull them from AdvancedSection's rendered mini charts (which show the
  //   correct plain vs revised data).
  // - Otherwise fall back to the primary chart alone.
  let idsToSnapshot;
  if (Array.isArray(chartElementIds) && chartElementIds.length) {
    idsToSnapshot = chartElementIds;
  } else if (advOn) {
    idsToSnapshot = ['advanced-actual-chart', 'advanced-revised-chart'];
  } else {
    idsToSnapshot = ['primary-chart'];
  }

  const chartImages = [];
  for (const id of idsToSnapshot) {
    const snap = await snapshot(id);
    if (snap) chartImages.push({ id, ...snap });
  }

  // ---- Page 1: Header + Loan Details + Summary --------------------------
  doc.setFillColor(1, 118, 211);
  doc.rect(0, 0, pageW, 60, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('EMIWise - Loan Summary', marginX, 30);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  doc.text(`Generated on ${dateStr}`, marginX, 48);

  let y = 82;

  // ---- Loan Details ----
  doc.setTextColor(3, 45, 96);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Loan Details', marginX, y);

  const dRate = (advanced.discountingRate == null || advanced.discountingRate === '')
    ? Number(interestRate) || 0
    : Number(advanced.discountingRate);

  autoTable(doc, {
    startY: y + 6,
    margin: { left: marginX, right: marginX },
    theme: 'grid',
    headStyles: { fillColor: [234, 245, 254], textColor: [3, 45, 96], fontStyle: 'bold' },
    styles: { fontSize: 9, textColor: [51, 65, 85], cellPadding: 4, overflow: 'linebreak' },
    columnStyles: {
      0: { fontStyle: 'bold', textColor: [3, 45, 96], cellWidth: 190, halign: 'left' },
      1: { cellWidth: 'auto', halign: 'right' },
    },
    head: [[{ content: 'Parameter', styles: { halign: 'left' } }, { content: 'Value', styles: { halign: 'right' } }]],
    body: [
      ['Currency', ascii(currency)],
      ['Interest method', interestMethod === 'flat' ? 'Flat rate' : 'Reducing balance'],
      ['Loan amount', pdfMoney(loanAmount, currency)],
      ['Interest rate (p.a.)', `${Number(interestRate).toFixed(2)} %`],
      ['Loan tenure', ascii(monthsToLabel(tenureMonths))],
      [
        'Moratorium',
        moratorium?.enabled
          ? `${moratorium.months} months (${moratorium.type === 'full' ? 'interest capitalised' : 'interest-only paid'})`
          : 'None',
      ],
      ['Credit days', advanced?.creditDaysEnabled ? `${advanced.creditDays} days @ ${dRate.toFixed(2)}%` : 'None'],
      ['Subvention', advanced?.subventionEnabled ? pdfMoney(advanced.subventionAmount, currency) : 'None'],
    ],
  });

  y = doc.lastAutoTable.finalY + 14;

  // ---- Summary ----
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(3, 45, 96);
  doc.text('Summary', marginX, y);

  const summaryBody = [
    ['Actual EMI (no advance options)', pdfMoney((plainSummary && plainSummary.emi) || summary.emi, currency)],
    ['EMI after advance options', pdfMoney(summary.emi, currency)],
  ];
  if (advOn && plainSummary) {
    const emiDelta = Math.max(0, (plainSummary.emi || 0) - (summary.emi || 0));
    summaryBody.push(['EMI saving per month (advance)', pdfMoney(emiDelta, currency)]);
  }
  summaryBody.push(['Effective ROI', `${Number(summary.effectiveAnnualRate || interestRate).toFixed(2)} %`]);

  // Prepayment details — monthly & one-time separately
  summaryBody.push(['Monthly extra prepayment', pdfMoney(extraEmi || 0, currency) + ' / month']);
  summaryBody.push(['Total monthly extra paid (over tenure)', pdfMoney(monthlyExtraTotal, currency)]);
  summaryBody.push([
    'One-time lump-sum prepayments',
    prepayments && prepayments.length
      ? `${prepayments.length} entries · ${pdfMoney(lumpSumTotal, currency)} total`
      : 'None',
  ]);
  summaryBody.push(['Total prepayments (monthly + one-time)', pdfMoney(prepayGrandTotal, currency)]);

  // Actual figures
  summaryBody.push(['Actual interest paid', pdfMoney(actualInterest, currency)]);
  summaryBody.push(['Actual amount to be paid', pdfMoney(actualTotalPayment, currency)]);
  summaryBody.push(['Actual payoff duration', ascii(monthsToLabel(summary.monthsTaken))]);

  autoTable(doc, {
    startY: y + 6,
    margin: { left: marginX, right: marginX },
    theme: 'grid',
    headStyles: { fillColor: [1, 118, 211], textColor: [255, 255, 255], fontStyle: 'bold' },
    styles: { fontSize: 9, textColor: [51, 65, 85], cellPadding: 4 },
    columnStyles: {
      0: { fontStyle: 'bold', textColor: [3, 45, 96], cellWidth: 260, halign: 'left' },
      1: { cellWidth: 'auto', halign: 'right' },
    },
    head: [[{ content: 'Metric', styles: { halign: 'left' } }, { content: 'Value', styles: { halign: 'right' } }]],
    body: summaryBody,
  });

  y = doc.lastAutoTable.finalY + 14;

  // ---- Charts — on Page 2 (Loan Breakup Charts always start a new page) ----
  let scheduleStartY;
  if (chartImages.length) {
    doc.addPage();
    let cy = 50;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(3, 45, 96);
    doc.text('Loan Breakup Charts', marginX, cy);
    cy += 12;

    const usableW = pageW - marginX * 2;
    const gap = 20;
    const cols = chartImages.length >= 2 ? 2 : 1;
    const cellW = (usableW - gap * (cols - 1)) / cols;
    const cellH = 190;

    const numbersById = {
      'primary-chart': {
        title: 'Loan Breakup',
        principal: loanAmount,
        interest: summary.totalInterest,
        emi: summary.emi,
        roi: Number(summary.effectiveAnnualRate || interestRate),
        moratoriumInterest: (moratorium?.enabled && moratorium?.type === 'full') ? (summary.moratoriumInterestCapitalised || 0) : 0,
      },
      'advanced-actual-chart': {
        title: 'Actual Representation',
        principal: loanAmount,
        interest: (plainSummary && plainSummary.totalInterest) != null ? plainSummary.totalInterest : summary.totalInterest,
        emi: (plainSummary && plainSummary.emi) != null ? plainSummary.emi : summary.emi,
        roi: Number(interestRate),
        moratoriumInterest: (moratorium?.enabled && moratorium?.type === 'full') ? ((plainSummary && plainSummary.moratoriumInterestCapitalised) || 0) : 0,
      },
      'advanced-revised-chart': {
        title: 'With Advance Options',
        principal: loanAmount,
        interest: summary.totalInterest,
        emi: summary.emi,
        roi: Number(summary.effectiveAnnualRate || interestRate),
        moratoriumInterest: (moratorium?.enabled && moratorium?.type === 'full') ? (summary.moratoriumInterestCapitalised || 0) : 0,
      },
    };

    chartImages.forEach((img, idx) => {
      const boxX = marginX + idx * (cellW + gap);
      const info = numbersById[img.id] || { title: '' };

      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.4);
      doc.roundedRect(boxX, cy, cellW, cellH, 5, 5);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(3, 45, 96);
      doc.text(info.title, boxX + 10, cy + 14);

      // Chart fills the full card width (no side legend)
      const availW = cellW - 20;
      const availH = cellH - 30;
      const imgAR = img.w / img.h;
      let drawW = availW;
      let drawH = drawW / imgAR;
      if (drawH > availH) {
        drawH = availH;
        drawW = drawH * imgAR;
      }
      const drawX = boxX + (cellW - drawW) / 2;
      const drawY = cy + 22 + (availH - drawH) / 2;
      doc.addImage(img.dataUrl, 'JPEG', drawX, drawY, drawW, drawH, undefined, 'FAST');
    });

    cy += cellH + 8;

    // P / I / Moratorium / EMI / ROI strip below — colours synced to the chart
    const rowH = 54;
    chartImages.forEach((img, idx) => {
      const boxX = marginX + idx * (cellW + gap);
      const info = numbersById[img.id] || { principal: 0, interest: 0, emi: 0 };
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.4);
      doc.roundedRect(boxX, cy, cellW, rowH, 5, 5);
      const items = [
        { label: 'Principal', value: pdfMoney(info.principal, currency), color: [1, 118, 211] },        // donut blue #0176D3
        { label: 'Interest', value: pdfMoney(info.interest, currency), color: [249, 112, 102] },        // donut coral #F97066
      ];
      if ((info.moratoriumInterest || 0) > 0) {
        items.push({ label: 'Moratorium', value: pdfMoney(info.moratoriumInterest, currency), color: [245, 158, 11] }); // donut amber #F59E0B
      }
      items.push({ label: 'EMI', value: pdfMoney(info.emi, currency), color: [14, 165, 255] });         // sky #0EA5FF
      if (info.roi != null) items.push({ label: 'ROI', value: `${Number(info.roi).toFixed(2)}%`, color: [5, 150, 105] }); // green #059669
      const colW = cellW / items.length;
      const valFont = items.length >= 5 ? 7.2 : 10;
      const labFont = items.length >= 5 ? 5.5 : 6.5;
      items.forEach((it, i) => {
        const cx = boxX + i * colW + colW / 2;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(labFont);
        doc.setTextColor(100, 116, 139);
        doc.text(it.label.toUpperCase(), cx, cy + 16, { align: 'center' });
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(valFont);
        doc.setTextColor(...it.color);
        doc.text(it.value, cx, cy + 36, { align: 'center' });
      });
      doc.setDrawColor(226, 232, 240);
      for (let i = 1; i < items.length; i++) {
        doc.line(boxX + i * colW, cy + 10, boxX + i * colW, cy + rowH - 10);
      }
    });

    // Amortization table starts RIGHT BELOW the chart strip on the same page
    scheduleStartY = cy + rowH + 18;
  } else {
    doc.addPage();
    scheduleStartY = 50;
  }

  // ---- Amortization Schedule ----
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(3, 45, 96);
  doc.text('Amortization Schedule', marginX, scheduleStartY);

  autoTable(doc, {
    startY: scheduleStartY + 6,
    margin: { left: marginX, right: marginX },
    theme: 'striped',
    headStyles: {
      fillColor: [1, 118, 211],
      textColor: [255, 255, 255],
      fontSize: 8,
      fontStyle: 'bold',
      halign: 'center',
      cellPadding: 4,
    },
    styles: {
      fontSize: 7.5,
      textColor: [51, 65, 85],
      cellPadding: 3,
      overflow: 'linebreak',
      lineColor: [226, 232, 240],
      lineWidth: 0.3,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    head: [['Month', 'Phase', 'Payment', 'Principal', 'Interest', 'Prepay', 'Balance']],
    body: schedule.map((r) => [
      String(r.month),
      ascii(r.phase),
      pdfMoney(r.emi, currency),
      pdfMoney(r.principalPaid, currency),
      pdfMoney(r.interestPaid, currency),
      r.prepayment ? pdfMoney(r.prepayment, currency) : '-',
      pdfMoney(r.balance, currency),
    ]),
    columnStyles: {
      0: { halign: 'center', cellWidth: 34 },
      1: { halign: 'left', cellWidth: 62 },
      2: { halign: 'right', cellWidth: 76 },
      3: { halign: 'right', cellWidth: 76 },
      4: { halign: 'right', cellWidth: 70 },
      5: { halign: 'right', cellWidth: 66 },
      6: { halign: 'right', cellWidth: 'auto' },
    },
    didDrawPage: () => {
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text('EMIWise - Illustrative only', marginX, pageH - 18);
    },
  });

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(`Page ${i} of ${pageCount}`, pageW - marginX, pageH - 18, { align: 'right' });
  }

  doc.save(`EMI-Summary-${Date.now()}.pdf`);
}

export default exportEmiPdf;
